import { useState, useContext, useCallback, useEffect, useMemo } from "react";
import { trpc } from "../../trpc";
import MerchantContext from "../../contexts/MerchantContext";
import Step1SelectPipeline from "./steps/Step1SelectPipeline";
import Step2SelectProject from "./steps/Step2SelectProject";
import StepWhatsappAndAgent from "./steps/StepWhatsappAndAgent";
import Step3SelectTemplate from "./steps/Step3SelectTemplate";
import Step4SelectLeads from "./steps/Step4SelectLeads";
import Step5Confirmation from "./steps/Step5Confirmation";
import StepPreselectedAudience from "./steps/StepPreselectedAudience";
import StepVariableMapping from "./steps/StepVariableMapping";
import StepPreselectedConfirm from "./steps/StepPreselectedConfirm";
import RecipientReviewPanel from "./RecipientReviewPanel";
import { chunk } from "./chunk";
import { resolveVariableValue, type VariableMapping } from "./variableMapping";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stepper, useSteps } from "@/components/ui/stepper";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import posthog from "@/lib/posthog";

// Surfaces the backend's TRPCError message (e.g. "La plantilla ya no existe en Meta...")
// as the toast description, falling back to the generic title when there is none.
const getServerErrorMessage = (error: unknown): string | undefined =>
  error instanceof Error && error.message ? error.message : undefined;

/**
 * What to send a preselected lead at: their number, else Meta's WhatsApp id.
 * Undefined when the lead carries neither and is therefore unreachable.
 *
 * Never the username — that is a lookup key, not an address, and the lead we
 * would look it up against is this one.
 */
const leadContact = (lead: {
  phone?: string | null;
  whatsappUserId?: string | null;
}): string | undefined => lead.phone?.trim() || lead.whatsappUserId?.trim() || undefined;

type CreateSendModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Preselected path (Tablero → Inactivos) — when supplied, the wizard skips
  // the CSV flow and builds broadcast recipients from these leads.
  initialLeadIds?: string[];
  initialLeadIdsLabel?: string;
};

const CreateSendModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialLeadIds,
  initialLeadIdsLabel,
}: CreateSendModalProps) => {
  const { merchantId, merchantType, displayName } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  const isPreselected = initialLeadIds !== undefined;
  const preselectedLeadIds = initialLeadIds ?? [];
  const preselectedLabel = initialLeadIdsLabel ?? "";

  const [csvPipelineId, setCsvPipelineId] = useState("");
  const [csvProjectId, setCsvProjectId] = useState("");
  const [csvWhatsappConnectionId, setCsvWhatsappConnectionId] = useState("");
  const [csvTemplateId, setCsvTemplateId] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [parsedCsvData, setParsedCsvData] = useState<
    Array<{
      contact: string;
      name: string;
      crmId?: string;
      templateVariables: Record<string, string>;
    }>
  >([]);
  const [broadcastName, setBroadcastName] = useState("");
  const [enableAiAgent, setEnableAiAgent] = useState(true);
  const [includeOtherEmployeeLeads, setIncludeOtherEmployeeLeads] = useState(false);
  const [includeAiHandledLeads, setIncludeAiHandledLeads] = useState(false);
  const [effectiveRecipientCount, setEffectiveRecipientCount] = useState<number | null>(null);

  // --- Preselected path state ---
  const [preWhatsappConnectionId, setPreWhatsappConnectionId] = useState("");
  const [preTemplateId, setPreTemplateId] = useState("");
  const [variableMapping, setVariableMapping] = useState<VariableMapping>({});

  const { data: connections, isLoading: isLoadingConnections } =
    trpc.whatsappConnections.list.useQuery(
      { merchantId: merchantId || "" },
      { enabled: !!merchantId }
    );

  const activeConnectionId = isPreselected ? preWhatsappConnectionId : csvWhatsappConnectionId;
  const selectedConnection = connections?.find((c) => c.id === activeConnectionId);

  const { data: templatesData } = trpc.whatsappTemplates.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && isPreselected }
  );
  const selectedTemplate = templatesData?.templates?.find((t) => t.id === preTemplateId);

  const templateVariables = useMemo(() => {
    if (!isPreselected || !selectedTemplate?.bodyPreview) return [];
    const matches = selectedTemplate.bodyPreview.matchAll(/\{\{(\w+)\}\}/g);
    return [...new Set([...matches].map((m) => m[1]))];
  }, [selectedTemplate, isPreselected]);

  // Initialize variable mapping when the template changes
  useEffect(() => {
    if (templateVariables.length === 0) return;
    const newMapping: VariableMapping = {};
    for (const v of templateVariables) {
      newMapping[v] = variableMapping[v] ?? {
        source: "lead_attribute",
        value: "name",
        fallback: "",
      };
    }
    setVariableMapping(newMapping);
  }, [templateVariables.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sample lead for previews (preselected path) — first id only, cheap.
  const { data: preselectedSample } = trpc.leads.getMany.useQuery(
    { merchantId: merchantId || "", ids: preselectedLeadIds.slice(0, 1) },
    {
      enabled: isPreselected && !!merchantId && preselectedLeadIds.length > 0,
      staleTime: 60_000,
    }
  );
  const preselectedSampleLead = preselectedSample?.leads?.[0];

  // Full lead set for the audience list and the recipient review panel
  // (preselected path). Chunked: a single getMany carrying every id overflows
  // the GET URL and is never sent (see ./chunk).
  const preselectedChunks = useMemo(() => chunk(preselectedLeadIds), [preselectedLeadIds]);
  const preselectedFullQueries = trpc.useQueries((t) =>
    preselectedChunks.map((ids) =>
      t.leads.getMany(
        { merchantId: merchantId || "", ids },
        { enabled: isPreselected && !!merchantId, staleTime: 60_000 }
      )
    )
  );
  const isLoadingPreselectedLeads = preselectedFullQueries.some((q) => q.isLoading);
  const preselectedLeads = preselectedFullQueries.flatMap((q) => q.data?.leads ?? []);
  const preselectedContacts = preselectedLeads.flatMap((lead) => leadContact(lead) ?? []);
  const csvContacts = useMemo(() => parsedCsvData.map((r) => r.contact), [parsedCsvData]);

  const steps = (() => {
    if (isPreselected) {
      return [
        { title: "Audiencia" },
        { title: "WhatsApp" },
        { title: "Plantilla" },
        { title: "Variables" },
        { title: "Confirmar" },
      ];
    }
    const s = [{ title: "Embudo" }];
    if (merchantType === "development") s.push({ title: "Proyecto" });
    s.push(
      { title: "WhatsApp" },
      { title: "Plantilla" },
      { title: "Archivo" },
      { title: "Confirmar" }
    );
    return s;
  })();

  const { activeStep, goToNext, goToPrevious, setActiveStep } = useSteps({
    count: steps.length,
  });

  const createBroadcastMutation = trpc.broadcast.create.useMutation();
  const [isResolvingRecipients, setIsResolvingRecipients] = useState(false);
  const isMutating = createBroadcastMutation.isLoading || isResolvingRecipients;

  const resetState = useCallback(() => {
    setActiveStep(0);
    setCsvPipelineId("");
    setCsvProjectId("");
    setCsvWhatsappConnectionId("");
    setCsvTemplateId("");
    setUploadedFile(null);
    setParsedCsvData([]);
    setBroadcastName("");
    setEnableAiAgent(true);
    setIncludeOtherEmployeeLeads(false);
    setIncludeAiHandledLeads(false);
    setEffectiveRecipientCount(null);
    setPreWhatsappConnectionId("");
    setPreTemplateId("");
    setVariableMapping({});
  }, [setActiveStep]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleCreateBroadcast = async () => {
    if (!merchantId || parsedCsvData.length === 0 || !broadcastName.trim()) return;

    try {
      await createBroadcastMutation.mutateAsync({
        merchantId,
        name: broadcastName.trim(),
        pipelineId: csvPipelineId,
        projectId: merchantType === "development" && csvProjectId ? csvProjectId : undefined,
        whatsappConnectionId: csvWhatsappConnectionId,
        templateId: csvTemplateId,
        enableAiAgent,
        includeOtherEmployeeLeads,
        includeAiHandledLeads,
        recipients: parsedCsvData,
      });

      posthog.capture("broadcast created", {
        merchant_id: merchantId,
        recipient_source: "csv",
        recipient_count: parsedCsvData.length,
        template_id: csvTemplateId,
      });
      toast.success("Envío masivo creado exitosamente");
      handleClose();
      onSuccess();
    } catch (error) {
      console.error("Error creating broadcast:", error);
      posthog.captureException(error);
      toast.error("Error al crear el envío masivo", {
        description: getServerErrorMessage(error),
      });
    }
  };

  // Resolves the selected leads into broadcast recipients client-side and
  // submits through the same broadcast.create used by the CSV path. Leads
  // still active on the chosen line are skipped later by the broadcast queue
  // (existingNotSentCount), so no extra exclusion is needed here.
  const handleCreatePreselected = async () => {
    if (!merchantId || preselectedLeadIds.length === 0 || !broadcastName.trim()) return;

    setIsResolvingRecipients(true);
    try {
      const leads = (
        await Promise.all(
          chunk(preselectedLeadIds).map((ids) => utils.leads.getMany.fetch({ merchantId, ids }))
        )
      ).flatMap((result) => result.leads);

      // A phoneless lead is still a recipient: the send layer reaches it by its
      // WhatsApp id, as Meta's `recipient`. Only a lead carrying neither handle
      // has no address at all, and those are the only ones dropped here.
      const recipients = leads.flatMap((lead) => {
        const contact = leadContact(lead);
        if (!contact) return [];

        return [
          {
            contact,
            name: lead.name?.trim() || "",
            templateVariables: Object.fromEntries(
              templateVariables.map((v) => [
                v,
                resolveVariableValue(variableMapping[v], lead, displayName || ""),
              ])
            ),
          },
        ];
      });

      await createBroadcastMutation.mutateAsync({
        merchantId,
        name: broadcastName.trim(),
        pipelineId: "default",
        whatsappConnectionId: preWhatsappConnectionId,
        templateId: preTemplateId,
        enableAiAgent,
        includeOtherEmployeeLeads,
        includeAiHandledLeads,
        recipients,
      });

      posthog.capture("broadcast created", {
        merchant_id: merchantId,
        recipient_source: "preselected",
        recipient_count: recipients.length,
        template_id: preTemplateId,
        audience_label: preselectedLabel,
      });
      toast.success("Envío creado exitosamente");
      handleClose();
      onSuccess();
    } catch (error) {
      console.error("Error creating preselected broadcast:", error);
      posthog.captureException(error);
      toast.error("Error al crear el envío", {
        description: getServerErrorMessage(error),
      });
    } finally {
      setIsResolvingRecipients(false);
    }
  };

  const isConfirmDisabled = () => !broadcastName.trim() || effectiveRecipientCount === 0;

  const isNextDisabled = (): boolean => {
    if (isPreselected) {
      switch (activeStep) {
        case 0:
          return preselectedLeadIds.length === 0;
        case 1:
          return !preWhatsappConnectionId;
        case 2:
          return !preTemplateId;
        case 3:
          return templateVariables.some((v) => !variableMapping[v]?.value);
        case 4:
          return isConfirmDisabled();
        default:
          return false;
      }
    }
    if (merchantType === "development") {
      switch (activeStep) {
        case 0:
          return !csvPipelineId;
        case 1:
          return false;
        case 2:
          return !csvWhatsappConnectionId;
        case 3:
          return !csvTemplateId;
        case 4:
          return !uploadedFile;
        case 5:
          return isConfirmDisabled();
        default:
          return false;
      }
    }
    switch (activeStep) {
      case 0:
        return !csvPipelineId;
      case 1:
        return !csvWhatsappConnectionId;
      case 2:
        return !csvTemplateId;
      case 3:
        return !uploadedFile;
      case 4:
        return isConfirmDisabled();
      default:
        return false;
    }
  };

  const isLastStep = activeStep === steps.length - 1;

  const renderConnectionsLoading = () => (
    <div className="flex min-h-[400px] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="xl" />
        <span className="text-gray-600">Cargando conexiones...</span>
      </div>
    </div>
  );

  const renderReviewPanel = (terms: string[], connectionId: string) => (
    <RecipientReviewPanel
      terms={terms}
      whatsappConnectionId={connectionId}
      includeOtherEmployee={includeOtherEmployeeLeads}
      includeAi={includeAiHandledLeads}
      onToggleOtherEmployee={setIncludeOtherEmployeeLeads}
      onToggleAi={setIncludeAiHandledLeads}
      onEffectiveCountChange={setEffectiveRecipientCount}
    />
  );

  const renderStepContent = () => {
    if (isPreselected) {
      switch (activeStep) {
        case 0:
          return (
            <StepPreselectedAudience
              leadCount={preselectedLeadIds.length}
              leads={preselectedLeads}
              isLoading={isLoadingPreselectedLeads}
              laneLabel={preselectedLabel}
            />
          );
        case 1:
          return (
            <StepWhatsappAndAgent
              enableAiAgent={enableAiAgent}
              onEnableAiAgentChange={setEnableAiAgent}
              selectedWhatsappConnectionId={preWhatsappConnectionId}
              onSelect={setPreWhatsappConnectionId}
            />
          );
        case 2:
          return isLoadingConnections ? (
            renderConnectionsLoading()
          ) : (
            <Step3SelectTemplate
              selectedTemplateId={preTemplateId}
              onSelect={setPreTemplateId}
              wabaId={selectedConnection?.wabaId}
            />
          );
        case 3:
          return (
            <StepVariableMapping
              templateVariables={templateVariables}
              variableMapping={variableMapping}
              onVariableMappingChange={setVariableMapping}
              selectedTemplateBodyText={selectedTemplate?.bodyPreview}
              sampleLead={preselectedSampleLead}
            />
          );
        case 4:
          return (
            <div className="flex flex-col gap-4">
              <StepPreselectedConfirm
                broadcastName={broadcastName}
                onBroadcastNameChange={setBroadcastName}
                recipientCount={preselectedLeadIds.length}
                audienceLabel={preselectedLabel}
                templateName={selectedTemplate?.name || preTemplateId}
                enableAiAgent={enableAiAgent}
                variableMapping={variableMapping}
                selectedTemplateBodyText={selectedTemplate?.bodyPreview}
                sampleLead={preselectedSampleLead}
                effectiveRecipientCount={effectiveRecipientCount}
              />
              {renderReviewPanel(preselectedContacts, preWhatsappConnectionId)}
            </div>
          );
        default:
          return null;
      }
    }

    if (merchantType === "development") {
      switch (activeStep) {
        case 0:
          return (
            <Step1SelectPipeline selectedPipelineId={csvPipelineId} onSelect={setCsvPipelineId} />
          );
        case 1:
          return <Step2SelectProject selectedProjectId={csvProjectId} onSelect={setCsvProjectId} />;
        case 2:
          return (
            <StepWhatsappAndAgent
              enableAiAgent={enableAiAgent}
              onEnableAiAgentChange={setEnableAiAgent}
              selectedWhatsappConnectionId={csvWhatsappConnectionId}
              onSelect={setCsvWhatsappConnectionId}
            />
          );
        case 3:
          return isLoadingConnections ? (
            renderConnectionsLoading()
          ) : (
            <Step3SelectTemplate
              selectedTemplateId={csvTemplateId}
              onSelect={setCsvTemplateId}
              wabaId={selectedConnection?.wabaId}
            />
          );
        case 4:
          return (
            <Step4SelectLeads
              selectedTemplateId={csvTemplateId}
              uploadedFile={uploadedFile}
              onFileUpload={setUploadedFile}
            />
          );
        case 5:
          return (
            <div className="flex flex-col gap-4">
              <Step5Confirmation
                selectedTemplateId={csvTemplateId}
                uploadedFile={uploadedFile}
                onCsvDataParsed={setParsedCsvData}
                broadcastName={broadcastName}
                onBroadcastNameChange={setBroadcastName}
                enableAiAgent={enableAiAgent}
                effectiveRecipientCount={effectiveRecipientCount}
              />
              {renderReviewPanel(csvContacts, csvWhatsappConnectionId)}
            </div>
          );
      }
    } else {
      switch (activeStep) {
        case 0:
          return (
            <Step1SelectPipeline selectedPipelineId={csvPipelineId} onSelect={setCsvPipelineId} />
          );
        case 1:
          return (
            <StepWhatsappAndAgent
              enableAiAgent={enableAiAgent}
              onEnableAiAgentChange={setEnableAiAgent}
              selectedWhatsappConnectionId={csvWhatsappConnectionId}
              onSelect={setCsvWhatsappConnectionId}
            />
          );
        case 2:
          return isLoadingConnections ? (
            renderConnectionsLoading()
          ) : (
            <Step3SelectTemplate
              selectedTemplateId={csvTemplateId}
              onSelect={setCsvTemplateId}
              wabaId={selectedConnection?.wabaId}
            />
          );
        case 3:
          return (
            <Step4SelectLeads
              selectedTemplateId={csvTemplateId}
              uploadedFile={uploadedFile}
              onFileUpload={setUploadedFile}
            />
          );
        case 4:
          return (
            <div className="flex flex-col gap-4">
              <Step5Confirmation
                selectedTemplateId={csvTemplateId}
                uploadedFile={uploadedFile}
                onCsvDataParsed={setParsedCsvData}
                broadcastName={broadcastName}
                onBroadcastNameChange={setBroadcastName}
                enableAiAgent={enableAiAgent}
                effectiveRecipientCount={effectiveRecipientCount}
              />
              {renderReviewPanel(csvContacts, csvWhatsappConnectionId)}
            </div>
          );
      }
    }

    return null;
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className="flex h-full max-h-full flex-col md:h-auto md:max-h-[85vh] md:max-w-4xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Crear Envío Masivo</DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          <Stepper activeStep={activeStep} steps={steps} size="sm" className="mb-4 md:mb-8" />

          {/* Step Content */}
          <div className="min-h-0 flex-1 overflow-y-auto pb-2 md:min-h-[400px]">
            {renderStepContent()}
          </div>

          <div className="flex justify-between border-t border-gray-200 pt-4">
            <Button variant="outline" onClick={goToPrevious} disabled={activeStep === 0}>
              Anterior
            </Button>

            <Button
              onClick={
                isLastStep
                  ? isPreselected
                    ? handleCreatePreselected
                    : handleCreateBroadcast
                  : goToNext
              }
              disabled={isNextDisabled() || isMutating}
            >
              {isMutating && <Spinner size="sm" className="text-white" />}
              {isLastStep ? (isPreselected ? "Crear envío" : "Enviar Mensajes") : "Continuar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSendModal;
